// src/services/api-auth.service.ts

import axios, { AxiosResponse } from 'axios';
import { authConfig, tokenStorage } from '../config/auth';
import { 
  UserType, 
  AuthResponse, 
  DefaultAdministratorResponse, 
  AdministratorResponse, 
  DriverResponse, 
  PassengerResponse,
  UnifiedUser,
  EmailLoginCredentials,
  IdentifierLoginCredentials,
  EmailPasswordReset,
  EmailPasswordResetConfirm,
  OtpPasswordReset,
  OtpPasswordResetConfirm
} from '../types/auth.types';

/**
 * Multi-Role API Authentication Service
 * Handles authentication for all user types via custom API endpoints
 */

// Create axios instance for authentication
const authApi = axios.create({
  baseURL: authConfig.apiBaseUrl,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add request interceptor to include auth token
authApi.interceptors.request.use(
  (config) => {
    const token = tokenStorage.getToken();
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add response interceptor to handle token expiration
authApi.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      
      // Try to refresh token if available
      const refreshSuccessful = await ApiAuthService.refreshToken();
      
      if (refreshSuccessful) {
        // Retry the original request with new token
        const token = tokenStorage.getToken();
        if (token) {
          originalRequest.headers.Authorization = `Bearer ${token}`;
          return authApi(originalRequest);
        }
      }
      
      // Token expired or invalid, clear storage and redirect
      tokenStorage.removeTokens();
      window.location.href = '/login';
    }
    
    return Promise.reject(error);
  }
);

export class ApiAuthService {
  /**
   * Multi-role login handler
   */
  static async login(
    userType: UserType, 
    credentials: EmailLoginCredentials | IdentifierLoginCredentials
  ): Promise<UnifiedUser> {
    try {
      const endpoint = this.getEndpointForUserType(userType);
      const requestPayload = this.formatLoginRequest(userType, credentials);
      
      const response: AxiosResponse<AuthResponse> = await authApi.post(
        endpoint,
        requestPayload
      );

      // Normalize response based on user type
      const normalizedUser = this.normalizeAuthResponse(userType, response.data);
      
      // Store user data and tokens
      this.storeUserData(normalizedUser);
      
      return normalizedUser;
    } catch (error: any) {
      console.error('API login error:', error);
      
      // Handle suspended accounts
      if (error.response?.status === 403) {
        throw new Error('Account suspended. Please contact support.');
      }
      
      // Handle specific API errors
      if (error.response?.status === 401) {
        throw new Error('Invalid credentials');
      } else if (error.response?.status === 429) {
        throw new Error('Too many login attempts. Please try again later.');
      } else if (error.response?.status >= 500) {
        throw new Error('Server error. Please try again later.');
      } else if (error.code === 'NETWORK_ERROR' || !error.response) {
        throw new Error('Network error. Please check your connection.');
      }
      
      throw new Error(error.response?.data?.message || 'Login failed. Please try again.');
    }
  }

  /**
   * Get appropriate endpoint for user type
   */
  private static getEndpointForUserType(userType: UserType): string {
    switch (userType) {
      case 'default-administrator':
        return authConfig.endpoints.defaultAdministrator;
      case 'administrator':
        return authConfig.endpoints.administrator;
      case 'driver':
        return authConfig.endpoints.driver;
      case 'passenger':
        return authConfig.endpoints.passenger;
      default:
        throw new Error(`Unsupported user type: ${userType}`);
    }
  }

  /**
   * Format login request based on user type
   */
  private static formatLoginRequest(
    userType: UserType, 
    credentials: EmailLoginCredentials | IdentifierLoginCredentials
  ): object {
    if (userType === 'default-administrator' || userType === 'administrator') {
      return {
        email: (credentials as EmailLoginCredentials).email,
        password: credentials.password
      };
    } else {
      return {
        identifier: (credentials as IdentifierLoginCredentials).identifier,
        password: credentials.password
      };
    }
  }

  /**
   * Normalize API response to unified user structure
   */
  private static normalizeAuthResponse(userType: UserType, response: AuthResponse): UnifiedUser {
    const baseUser = {
      userType,
      isActive: true,
      isSuspended: false,
      profileImageUrl: null,
    };

    switch (userType) {
      case 'default-administrator':
        const defaultAdminResponse = response as DefaultAdministratorResponse;
        return {
          ...baseUser,
          id: 'default-admin',
          email: defaultAdminResponse.email,
          accessToken: defaultAdminResponse.accessToken,
          refreshToken: undefined,
        };

      case 'administrator':
        const adminResponse = response as AdministratorResponse;
        return {
          ...baseUser,
          id: adminResponse._id,
          email: adminResponse.email,
          role: adminResponse.role,
          isSuspended: adminResponse.isSuspended,
          accessToken: adminResponse.accessToken,
          refreshToken: adminResponse.refreshToken,
          createdAt: adminResponse.createdAt,
          updatedAt: adminResponse.updatedAt,
        };

      case 'driver':
        const driverResponse = response as DriverResponse;
        return {
          ...baseUser,
          id: driverResponse._id,
          identifier: driverResponse.identifier,
          email: driverResponse.email,
          username: driverResponse.username,
          phone: driverResponse.phone,
          profileImageUrl: driverResponse.profileImageUrl,
          isSuspended: driverResponse.isSuspended,
          accessToken: driverResponse.accessToken,
          refreshToken: driverResponse.refreshToken,
          vehicleInfo: driverResponse.vehicleInfo,
          licenseInfo: driverResponse.licenseInfo,
          createdAt: driverResponse.createdAt,
          updatedAt: driverResponse.updatedAt,
        };

      case 'passenger':
        const passengerResponse = response as PassengerResponse;
        return {
          ...baseUser,
          id: passengerResponse._id,
          identifier: passengerResponse.identifier,
          username: passengerResponse.username,
          phone: passengerResponse.phone,
          email: passengerResponse.email,
          profileImageUrl: passengerResponse.profileImageUrl,
          isSuspended: passengerResponse.isSuspended,
          accessToken: passengerResponse.accessToken,
          refreshToken: passengerResponse.refreshToken,
          createdAt: passengerResponse.createdAt,
          updatedAt: passengerResponse.updatedAt,
        };

      default:
        throw new Error(`Unsupported user type: ${userType}`);
    }
  }

  /**
   * Store user data in localStorage
   */
  private static storeUserData(user: UnifiedUser): void {
    tokenStorage.setToken(user.accessToken);
    tokenStorage.setUserType(user.userType);
    tokenStorage.setUserProfile(JSON.stringify(user));
    
    if (user.refreshToken) {
      tokenStorage.setRefreshToken(user.refreshToken);
    }
    
    if (user.email) {
      tokenStorage.setUserEmail(user.email);
    }
    
    if (user.identifier) {
      tokenStorage.setUserIdentifier(user.identifier);
    }
  }

  /**
   * Forgot password request based on user type
   */
  static async forgotPassword(userType: UserType, data: EmailPasswordReset | OtpPasswordReset): Promise<void> {
    try {
      let endpoint: string;
      switch (userType) {
        case 'administrator':
          endpoint = authConfig.forgotPasswordEndpoints.administrator;
          break;
        case 'driver':
          endpoint = authConfig.forgotPasswordEndpoints.driver;
          break;
        case 'passenger':
          endpoint = authConfig.forgotPasswordEndpoints.passenger;
          break;
        case 'default-administrator':
          // Default administrator uses the same endpoint as administrator for forgot password
          endpoint = authConfig.forgotPasswordEndpoints.administrator;
          break;
        default:
          throw new Error(`Unsupported user type: ${userType}`);
      }
      
      await authApi.post(endpoint, data);
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Password reset request failed');
    }
  }

  /**
   * Reset password confirmation based on user type
   */
  static async resetPassword(userType: UserType, data: EmailPasswordResetConfirm | OtpPasswordResetConfirm): Promise<void> {
    try {
      let endpoint: string;
      switch (userType) {
        case 'administrator':
          endpoint = authConfig.passwordResetEndpoints.administrator;
          break;
        case 'driver':
          endpoint = authConfig.passwordResetEndpoints.driver;
          break;
        case 'passenger':
          endpoint = authConfig.passwordResetEndpoints.passenger;
          break;
        case 'default-administrator':
          // Default administrator uses the same endpoint as administrator for password reset
          endpoint = authConfig.passwordResetEndpoints.administrator;
          break;
        default:
          throw new Error(`Unsupported user type: ${userType}`);
      }
      
      await authApi.post(endpoint, data);
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Password reset failed');
    }
  }

  /**
   * Legacy email-based password reset methods for backward compatibility
   */
  static async forgotPasswordEmail(data: EmailPasswordReset): Promise<void> {
    return this.forgotPassword('administrator', data);
  }

  static async resetPasswordEmail(data: EmailPasswordResetConfirm): Promise<void> {
    return this.resetPassword('administrator', data);
  }

  static async forgotPasswordOTP(data: OtpPasswordReset): Promise<void> {
    return this.forgotPassword('driver', data);
  }

  static async resetPasswordOTP(data: OtpPasswordResetConfirm): Promise<void> {
    return this.resetPassword('driver', data);
  }

  /**
   * Logout user
   */
  static async logout(): Promise<void> {
    try {
      // Clear stored tokens
      tokenStorage.removeTokens();
      
      // Optional: Call logout endpoint if available
      // await authApi.post('/auth/logout');
    } catch (error) {
      console.error('Logout error:', error);
      // Still clear tokens even if API call fails
      tokenStorage.removeTokens();
    }
  }

  /**
   * Check if user is authenticated
   */
  static isAuthenticated(): boolean {
    const token = tokenStorage.getToken();
    if (!token) return false;

    try {
      // Basic JWT structure validation
      const parts = token.split('.');
      if (parts.length !== 3) return false;

      // Decode payload to check expiration
      const payload = JSON.parse(atob(parts[1]));
      const currentTime = Math.floor(Date.now() / 1000);

      // Check if token is expired
      if (payload.exp && payload.exp < currentTime) {
        tokenStorage.removeTokens();
        return false;
      }

      return true;
    } catch (error) {
      console.error('Token validation error:', error);
      tokenStorage.removeTokens();
      return false;
    }
  }

  /**
   * Get current user information from stored data
   */
  static getCurrentUser(): UnifiedUser | null {
    const userProfile = tokenStorage.getUserProfile();
    const token = tokenStorage.getToken();
    
    if (!userProfile || !token || !this.isAuthenticated()) {
      return null;
    }

    try {
      return JSON.parse(userProfile);
    } catch (error) {
      console.error('Error parsing user profile:', error);
      return null;
    }
  }

  /**
   * Get authorization headers for API requests
   */
  static getAuthHeaders(): { Authorization: string } | {} {
    const token = tokenStorage.getToken();
    return token ? { Authorization: `Bearer ${token}` } : {};
  }

  /**
   * Refresh token if supported by API
   */
  static async refreshToken(): Promise<boolean> {
    try {
      const refreshToken = tokenStorage.getRefreshToken();
      const userType = tokenStorage.getUserType();
      
      // Skip refresh for default-administrator (no refresh token)
      if (!refreshToken || userType === 'default-administrator') {
        return false;
      }
      
      let endpoint: string;
      switch (userType) {
        case 'administrator':
          endpoint = authConfig.refreshTokenEndpoints.administrator;
          break;
        case 'driver':
          endpoint = authConfig.refreshTokenEndpoints.driver;
          break;
        case 'passenger':
          endpoint = authConfig.refreshTokenEndpoints.passenger;
          break;
        default:
          console.error('Unsupported user type for token refresh:', userType);
          return false;
      }
      
      const response = await authApi.post(endpoint, { refreshToken });
      
      if (response.data.accessToken) {
        tokenStorage.setToken(response.data.accessToken);
        
        // Update refresh token if provided
        if (response.data.refreshToken) {
          tokenStorage.setRefreshToken(response.data.refreshToken);
        }
        
        return true;
      }
      
      return false;
    } catch (error) {
      console.error('Token refresh error:', error);
      return false;
    }
  }

  /**
   * Check if user account is suspended
   */
  static isUserSuspended(): boolean {
    const user = this.getCurrentUser();
    return user ? user.isSuspended : false;
  }

  /**
   * Legacy method for backward compatibility
   */
  static async loginLegacy(email: string, password: string): Promise<{ email: string; accessToken: string }> {
    const user = await this.login('default-administrator', { email, password });
    return {
      email: user.email || email,
      accessToken: user.accessToken
    };
  }
}

export default ApiAuthService;